import api from './api';

export interface FilterResultsDto {
  round?: string;
  period?: string; // YYYYMM 형식, 'all'은 전체 선택
  company?: string;
  team?: string;
}

export interface StatsResponse {
  total: number;
  unread: number;
  emailOpened: number;
  linkClicked: number;
  infoInput: number;
  spamReport?: number;
  phishing?: number;
  spam?: number;
}

export interface SpamReporter {
  company: string;
  team: string;
  name: string;
  category: string;
}

export interface ResultRow {
  id: number;
  round: string;
  period: string;
  company: string;
  team: string | null;
  name: string;
  email: string;
  result: number;
  reportCategory?: string | null;
}

export const resultsService = {
  getStats: async (filters: FilterResultsDto): Promise<StatsResponse> => {
    const params = new URLSearchParams();
    if (filters.round) params.append('round', filters.round);
    if (filters.period && filters.period !== 'all') params.append('period', filters.period);
    if (filters.company) params.append('company', filters.company);
    if (filters.team) params.append('team', filters.team);

    const response = await api.get<StatsResponse>(`/results/stats?${params.toString()}`);
    return response.data;
  },

  getAvailableRounds: async (): Promise<string[]> => {
    const response = await api.get<string[]>('/results/rounds');
    return response.data;
  },

  getAvailablePeriods: async (round?: string, company?: string): Promise<string[]> => {
    const params = new URLSearchParams();
    if (round) params.append('round', round);
    if (company) params.append('company', company);
    const queryString = params.toString();
    const response = await api.get<string[]>(`/results/periods${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },

  getAvailableCompanies: async (round?: string, period?: string): Promise<string[]> => {
    const params = new URLSearchParams();
    if (round) params.append('round', round);
    if (period && period !== 'all') params.append('period', period);
    const queryString = params.toString();
    const response = await api.get<string[]>(`/results/companies${queryString ? `?${queryString}` : ''}`);
    return response.data;
  },

  getAvailableTeams: async (round?: string, company?: string, period?: string): Promise<string[]> => {
    const params = new URLSearchParams();
    if (round) params.append('round', round);
    if (period && period !== 'all') params.append('period', period);
    if (company) params.append('company', company);
    const response = await api.get<string[]>(`/results/teams?${params.toString()}`);
    return response.data;
  },

  exportToExcel: async (filters: FilterResultsDto, password: string): Promise<Blob> => {
    const params = new URLSearchParams();
    if (filters.round) params.append('round', filters.round);
    if (filters.period && filters.period !== 'all') params.append('period', filters.period);
    if (filters.company) params.append('company', filters.company);
    if (filters.team) params.append('team', filters.team);

    const response = await api.post(
      `/results/export?${params.toString()}`,
      { password },
      {
        responseType: 'blob',
      }
    );
    return response.data;
  },

  importSpamReport: async (file: File, round: string, period?: string): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('round', round);
    if (period) {
      formData.append('period', period);
    }

    // FormData를 사용하면 axios가 자동으로 multipart/form-data로 설정하고
    // boundary를 추가합니다. Content-Type을 명시하면 boundary가 제대로 설정되지 않을 수 있습니다.
    // 토큰은 api.ts의 interceptor에서 자동으로 추가됩니다.
    const response = await api.post('/results/import-spam-report', formData);
    return response.data;
  },

  uploadAptDataCsv: async (file: File, round: string, period: string, deleteExisting: boolean = false): Promise<any> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('round', round);
    formData.append('period', period);
    formData.append('deleteExisting', deleteExisting.toString());

    const response = await api.post('/results/admin/upload-csv', formData);
    return response.data;
  },

  // 상세 데이터: GET /results (회사/팀/이름/이메일/result 복호화 반환)
  getResults: async (filters: FilterResultsDto): Promise<ResultRow[]> => {
    const params = new URLSearchParams();
    if (filters.round) params.append('round', filters.round);
    if (filters.period && filters.period !== 'all') params.append('period', filters.period);
    if (filters.company) params.append('company', filters.company);
    if (filters.team) params.append('team', filters.team);

    const response = await api.get<ResultRow[]>(`/results?${params.toString()}`);
    return response.data;
  },

  getSpamReporters: async (filters: FilterResultsDto): Promise<SpamReporter[]> => {
    const params = new URLSearchParams();
    if (filters.round) params.append('round', filters.round);
    if (filters.period && filters.period !== 'all') params.append('period', filters.period);
    if (filters.company) params.append('company', filters.company);
    if (filters.team) params.append('team', filters.team);

    const response = await api.get<SpamReporter[]>(`/results/spam-reporters?${params.toString()}`);
    return response.data;
  },
};

